import { isDefined, isEmpty, type RoleId, type SmallUser, type TinyRole, type UserId } from '@terros/common'
import { type QueryClient, type QueryFilters, type QueryKey } from '@tanstack/react-query'
import { userListKey } from '../keys'

/**
 * Role and team-member streams reindex the user document asynchronously.
 * Refetching immediately can read the old roles and overwrite a local patch.
 */
export const USER_LIST_CONVERGE_DELAY_MS = 5_000

type ListedRoleUpdate = (roles: TinyRole[]) => TinyRole[]

type RoleIdChange = {
  roles: TinyRole[]
  nextRoleIds: RoleId[] | undefined
  resolveRole: (roleId: RoleId) => TinyRole | undefined
}

const convergeTimers = new Map<UserId, NodeJS.Timeout>()

/**
 * `['users']` is the prefix of every `/user/list` key (`['users', UserListInput]`).
 * It is also the prefix of the Sunrun Okta query (`['users', 'sunrun-okta']`), which is not a user list.
 */
export function isUserListQueryKey(queryKey: QueryKey): boolean {
  if (queryKey[0] !== 'users') return false
  const input = queryKey[1]
  if (input === undefined) return queryKey.length === 1
  return isPlainRecord(input)
}

export function userListQueriesFilter(): QueryFilters {
  return {
    queryKey: userListKey(),
    predicate: (query) => isUserListQueryKey(query.queryKey),
  }
}

export function withoutRole(roles: TinyRole[], roleId: RoleId): TinyRole[] {
  if (!roles.some((role) => role.roleId === roleId)) return roles
  return roles.filter((role) => role.roleId !== roleId)
}

export function withRole(roles: TinyRole[], role: TinyRole): TinyRole[] {
  if (roles.some((existing) => existing.roleId === role.roleId)) return roles
  return [...roles, role]
}

/** Adds roles from the new membership. A removed team role can briefly stay visible until the delayed refetch. */
export function applyRoleIdChange({ roles, nextRoleIds, resolveRole }: RoleIdChange): TinyRole[] {
  if (isEmpty(nextRoleIds)) return roles
  return nextRoleIds.reduce((current, roleId) => {
    const role = resolveRole(roleId)
    if (!role) return current
    return withRole(current, role)
  }, roles)
}

export function patchListedUserRoles({
  users,
  userId,
  update,
}: {
  users: SmallUser[] | undefined
  userId: UserId
  update: ListedRoleUpdate
}): SmallUser[] | undefined {
  if (!users) return
  const index = users.findIndex((user) => user.userId === userId)
  const user = users[index]
  if (!user) return users
  const current = user.roles ?? []
  const roles = update(current)
  if (roles === current) return users
  return users.map((item, itemIndex) => (itemIndex === index ? { ...item, roles } : item))
}

export function patchUserListQueries({
  queryClient,
  userId,
  update,
}: {
  queryClient: QueryClient
  userId: UserId
  update: ListedRoleUpdate
}): void {
  queryClient.setQueriesData<SmallUser[]>(userListQueriesFilter(), (existing) =>
    patchListedUserRoles({ users: existing, userId, update })
  )
}

export function invalidateUserListQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries(userListQueriesFilter())
}

export function syncListedUserRoles({
  queryClient,
  userId,
  update,
}: {
  queryClient: QueryClient
  userId: UserId
  update: ListedRoleUpdate
}): void {
  // Drop in-flight /user/list fetches before the patch so a late response cannot replace it.
  void queryClient.cancelQueries(userListQueriesFilter())
  patchUserListQueries({ queryClient, userId, update })
  scheduleUserListConverge(queryClient, userId)
}

function scheduleUserListConverge(queryClient: QueryClient, userId: UserId): void {
  const pending = convergeTimers.get(userId)
  if (isDefined(pending)) clearTimeout(pending)
  const timer = setTimeout(() => {
    if (convergeTimers.get(userId) !== timer) return
    convergeTimers.delete(userId)
    invalidateUserListQueries(queryClient)
  }, USER_LIST_CONVERGE_DELAY_MS)
  convergeTimers.set(userId, timer)
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object') return false
  if (value === null) return false
  return !Array.isArray(value)
}
